/**
 * TettoAiAziendaCard — il tetto giornaliero delle funzioni AI di UNA azienda, nella sua scheda (22/10/2026,
 * `shared/strumenti/tettoAiGiornaliero.ts`): quanto vale oggi (eccezione o piano), quanto ha speso, e l'eccezione da
 * mettere o togliere. Le stesse funzioni della scheda «Tetto AI giornaliero» della pagina dei piani.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, Stack, TextField, Typography } from '@mui/material';
import { tokens } from '../../theme';
import { eurDaCentesimi } from './testiStrumenti';
import { centesimiDaTesto } from './TettoAiGiornalieroTab';
import { leggiSpesaAiDiOggi, leggiTettoAi, salvaTettoAi } from '../../services/tettoAiService';
import {
  CAPABILITY_SENZA_LIMITE_GIORNALIERO,
  centesimiDaMicrousd,
  tettoInCentesimi,
  type ConfigTettoAi,
} from '@shared/strumenti/tettoAiGiornaliero';

interface Props {
  tenant: { id: string; tier?: string | null; modules?: { base?: unknown; addon?: unknown; override?: unknown } | null };
}

const haLaCapability = (m: Props['tenant']['modules'], cap: string): boolean =>
  ['base', 'addon', 'override'].some((k) => {
    const v = (m as Record<string, unknown> | null | undefined)?.[k];
    return Array.isArray(v) ? v.includes(cap) : !!(v && typeof v === 'object' && (v as Record<string, unknown>)[cap]);
  });

export const TettoAiAziendaCard: React.FC<Props> = ({ tenant }) => {
  const [config, setConfig] = useState<ConfigTettoAi | null>(null);
  const [spesiOggi, setSpesiOggi] = useState(0);
  const [testo, setTesto] = useState('');
  const [caricamento, setCaricamento] = useState(true);
  const [occupato, setOccupato] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  const carica = useCallback(async () => {
    setCaricamento(true);
    setErrore(null);
    try {
      const [letta, spesa] = await Promise.all([leggiTettoAi(), leggiSpesaAiDiOggi()]);
      setConfig(letta.config);
      const mia = spesa.find((r) => r.tenant_id === tenant.id);
      setSpesiOggi(mia && letta.config ? centesimiDaMicrousd(mia.microusd, letta.config.eur_per_usd) : 0);
    } catch (e) {
      setErrore(e instanceof Error ? e.message : String(e));
    } finally {
      setCaricamento(false);
    }
  }, [tenant.id]);

  useEffect(() => { void carica(); }, [carica]);

  const senzaLimite = haLaCapability(tenant.modules, CAPABILITY_SENZA_LIMITE_GIORNALIERO);
  const eccezione = config ? config.eccezioni[tenant.id] : undefined;
  const tetto = config ? tettoInCentesimi(config, { tenantId: tenant.id, piano: String(tenant.tier ?? 'base') }) : null;

  const salva = async (centesimi: number | null) => {
    setOccupato(true);
    setErrore(null);
    try {
      setConfig(await salvaTettoAi({ eccezione: { tenant_id: tenant.id, centesimi } }));
      setTesto('');
    } catch (e) {
      setErrore(e instanceof Error ? e.message : String(e));
    } finally {
      setOccupato(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mt: 3 }}>
      <Typography sx={{ fontWeight: 700, mb: 1 }}>Tetto giornaliero delle funzioni AI</Typography>
      {caricamento ? (
        <Box sx={{ py: 2 }}><CircularProgress size={22} /></Box>
      ) : senzaLimite ? (
        <Typography variant="body2" sx={{ color: tokens.color.ash }}>
          Questa azienda non ha tetto: ha «Assistente senza limite giornaliero» (piano Business o aggiunta).
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {errore && <Alert severity="error" onClose={() => setErrore(null)}>{errore}</Alert>}
          <Typography variant="body2">
            Tetto di oggi: <b>{tetto === null ? 'non configurato (Yuma si ferma)' : eurDaCentesimi(tetto)}</b>
            {typeof eccezione === 'number' ? ' (eccezione di questa azienda)' : ' (dal suo piano)'} · spesa di oggi: <b>{eurDaCentesimi(spesiOggi)}</b>
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <TextField size="small" label="Eccezione (€ al giorno)" value={testo} onChange={(e) => setTesto(e.target.value)} inputProps={{ inputMode: 'decimal' }} />
            <Button
              variant="outlined"
              disabled={occupato}
              sx={{ minHeight: 44 }}
              onClick={() => {
                const c = centesimiDaTesto(testo);
                if (c === undefined || c === null) setErrore('Scrivi il tetto in euro, per esempio 2,50');
                else void salva(c);
              }}
            >
              Salva l'eccezione
            </Button>
            {typeof eccezione === 'number' && (
              <Button disabled={occupato} sx={{ minHeight: 44 }} onClick={() => void salva(null)}>Torna al tetto del piano</Button>
            )}
          </Stack>
        </Stack>
      )}
    </Paper>
  );
};

export default TettoAiAziendaCard;
